import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { apiFetch } from '@/lib/api'

import type {
  VacationBalanceByType,
  VacationRequest,
  VacationStatus,
  VacationType,
} from '@/features/vacation/use-vacation'

export interface AdminVacationUser {
  id: string
  name: string
  email: string
  position: { name: string } | null
}

export interface AdminVacationRequest extends VacationRequest {
  user: AdminVacationUser
}

export interface AdminVacationBalanceEntry {
  user: AdminVacationUser
  balance: {
    totalDays: number
    usedDays: number
    pendingDays: number
    remainingDays: number
  }
  byType: VacationBalanceByType
}

export interface AdminVacationBalancesResponse {
  year: number
  balances: AdminVacationBalanceEntry[]
}

export const ADMIN_VACATION_KEY = ['admin', 'vacation'] as const

export interface AdminRequestsFilter {
  status?: VacationStatus
  userId?: string
  year?: number
  /**
   * Zakres dat — zwraca wnioski nakładające się na [from, to].
   * Kalendarz używa tego zamiast `year`, bo filtr roczny działa po `startDate`
   * i gubi wnioski przechodzące przez sylwestra (tydzień 28.12–01.01,
   * albo siatka miesiąca zaczynająca się w poprzednim roku).
   */
  from?: string
  to?: string
}

export interface UseAdminVacationRequestsOptions {
  /** Patrz `useAdminSessions` — płynne przełączanie zakresów w kalendarzu. */
  keepPrevious?: boolean
}

export function useAdminVacationRequests(
  filters?: AdminRequestsFilter,
  options?: UseAdminVacationRequestsOptions,
) {
  return useQuery({
    ...(options?.keepPrevious ? { placeholderData: keepPreviousData } : {}),
    queryKey: [...ADMIN_VACATION_KEY, 'requests', filters ?? null],
    queryFn: () => {
      const qs = new URLSearchParams()
      if (filters?.status) qs.set('status', filters.status)
      if (filters?.userId) qs.set('userId', filters.userId)
      if (filters?.year !== undefined) qs.set('year', String(filters.year))
      if (filters?.from) qs.set('from', filters.from)
      if (filters?.to) qs.set('to', filters.to)
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return apiFetch<{ requests: AdminVacationRequest[] }>(
        `/api/admin/vacation/requests${suffix}`,
      )
    },
    staleTime: 30_000,
  })
}

export function useAdminVacationBalances(year?: number) {
  return useQuery({
    queryKey: [...ADMIN_VACATION_KEY, 'balances', year ?? 'current'],
    queryFn: () => {
      const qs = year !== undefined ? `?year=${year}` : ''
      return apiFetch<AdminVacationBalancesResponse>(
        `/api/admin/vacation/balances${qs}`,
      )
    },
    staleTime: 30_000,
  })
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ADMIN_VACATION_KEY })
  qc.invalidateQueries({ queryKey: ['vacation'] })
  qc.invalidateQueries({ queryKey: ['admin-users'] })
  // Raport miesięczny i karta progresu liczą teraz godziny z urlopów, więc
  // każda decyzja o wniosku zmienia ich wynik. Bez tego admin wracał do raportu
  // ze `staleTime: 30 s` i widział liczby sprzed zatwierdzenia.
  qc.invalidateQueries({ queryKey: ['admin', 'reports'] })
  qc.invalidateQueries({ queryKey: ['my-monthly-progress'] })
}

export interface ApproveVacationBody {
  id: string
  decisionNote?: string
}

export function useApproveVacationRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, decisionNote }: ApproveVacationBody) =>
      apiFetch<{ request: AdminVacationRequest }>(
        `/api/admin/vacation/requests/${id}/approve`,
        // Zawsze z body — POST bez body daje request.body = null po stronie Fastify
        // i wywala walidację Zod ("Expected object, received null").
        { method: 'POST', body: decisionNote !== undefined ? { decisionNote } : {} },
      ),
    onSuccess: () => invalidateAll(qc),
  })
}

export interface RejectVacationBody {
  id: string
  decisionNote: string
}

export function useRejectVacationRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, decisionNote }: RejectVacationBody) =>
      apiFetch<{ request: AdminVacationRequest }>(
        `/api/admin/vacation/requests/${id}/reject`,
        { method: 'POST', body: { decisionNote } },
      ),
    onSuccess: () => invalidateAll(qc),
  })
}

export interface AdminCreateVacationBody {
  userId: string
  type: VacationType
  startDate: string
  endDate: string
  comment?: string
}

export function useAdminCreateVacationRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: AdminCreateVacationBody) =>
      apiFetch<{ request: AdminVacationRequest; quotaWarning: string | null }>(
        '/api/admin/vacation/requests',
        { method: 'POST', body: { ...body } },
      ),
    onSuccess: () => invalidateAll(qc),
  })
}

export interface AdminUpdateVacationBody {
  id: string
  type?: VacationType
  startDate?: string
  endDate?: string
  comment?: string | null
}

export function useAdminUpdateVacationRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...patch }: AdminUpdateVacationBody) =>
      apiFetch<{ request: AdminVacationRequest }>(
        `/api/admin/vacation/requests/${id}`,
        { method: 'PATCH', body: { ...patch } },
      ),
    onSuccess: () => invalidateAll(qc),
  })
}

export function useAdminDeleteVacationRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<null>(`/api/admin/vacation/requests/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => invalidateAll(qc),
  })
}

export interface UpdateVacationBalanceBody {
  userId: string
  year: number
  totalDays?: number
  usedDays?: number
}

export function useUpdateVacationBalance() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, year, ...patch }: UpdateVacationBalanceBody) =>
      apiFetch<{
        balance: { userId: string; year: number; totalDays: number; usedDays: number }
      }>(`/api/admin/vacation/balances/${userId}/${year}`, {
        method: 'PUT',
        body: { ...patch },
      }),
    onSuccess: () => invalidateAll(qc),
  })
}
